import { useQuery } from '@tanstack/react-query';
import { apiGet } from './client';

export interface AuditActor {
  id: string;
  email: string;
  displayName: string;
}

export interface AuditRow {
  id: string;
  actorId: string;
  actor: AuditActor;
  action: string;
  target: string | null;
  metadata: unknown;
  at: string;
}

export interface AuditListEnvelope {
  items: AuditRow[];
  total: number;
  limit: number;
  offset: number;
}

export interface AuditFilters {
  actor?: string;
  action?: string;
  target?: string;
  from?: string; // ISO date string
  to?: string;
  page: number; // 1-based
}

const PAGE_SIZE = 25;
const KEYS = {
  list: (filters: AuditFilters) => ['audit-log', 'list', filters] as const,
  actors: () => ['audit-log', 'actors'] as const,
  actions: () => ['audit-log', 'actions'] as const,
};

export function useAuditLog(filters: AuditFilters) {
  return useQuery({
    queryKey: KEYS.list(filters),
    queryFn: () => {
      const offset = (filters.page - 1) * PAGE_SIZE;
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
      if (filters.actor) params.set('actor', filters.actor);
      if (filters.action) params.set('action', filters.action);
      if (filters.target) params.set('target', filters.target);
      if (filters.from) params.set('from', filters.from);
      if (filters.to) params.set('to', filters.to);
      return apiGet<AuditListEnvelope>(`/api/audit-log?${params.toString()}`);
    },
    placeholderData: (prev) => prev,
  });
}

export function useAuditActors() {
  return useQuery({
    queryKey: KEYS.actors(),
    queryFn: () => apiGet<AuditActor[]>('/api/audit-log/actors'),
    staleTime: 60_000,
  });
}

export function useAuditActions() {
  return useQuery({
    queryKey: KEYS.actions(),
    queryFn: () => apiGet<string[]>('/api/audit-log/actions'),
    staleTime: 60_000,
  });
}
